import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Check, X } from "lucide-react";

export default function RFIQuestionItem({ question, onUpdate, onDelete, isEditing = true }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState({
    question_text: question.question_text || "",
    status: question.status || "Open",
    response: question.response || ""
  });

  const handleSave = () => {
    onUpdate(question.id, editedQuestion);
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setEditedQuestion({
      question_text: question.question_text || "",
      status: question.status || "Open",
      response: question.response || ""
    });
    setIsEditMode(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      "Open": "bg-blue-100 text-blue-800",
      "Partially Closed": "bg-yellow-100 text-yellow-800",
      "Closed": "bg-green-100 text-green-800",
      "Follow-up": "bg-purple-100 text-purple-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (!isEditing) {
    // View-only mode
    return (
      <div className="border rounded-lg p-4 bg-white">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-blue-600">{question.question_number}</span>
            <Badge className={getStatusColor(question.status)}>
              {question.status}
            </Badge>
          </div>
        </div>
        <div className="space-y-2">
          <div>
            <p className="text-sm font-medium text-gray-600">Question:</p>
            <p className="text-sm">{question.question_text || "(No question text)"}</p>
          </div>
          {question.response && (
            <div>
              <p className="text-sm font-medium text-gray-600">Response:</p>
              <p className="text-sm">{question.response}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isEditMode) {
    // Edit mode
    return (
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-blue-600">{question.question_number}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={handleSave}>
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel}>
              <X className="h-4 w-4 text-red-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(question.id)}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Question</label>
            <Textarea
              value={editedQuestion.question_text}
              onChange={(e) => setEditedQuestion({ ...editedQuestion, question_text: e.target.value })}
              placeholder="Enter question text..."
              rows={2}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
            <Select
              value={editedQuestion.status}
              onValueChange={(value) => setEditedQuestion({ ...editedQuestion, status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Partially Closed">Partially Closed</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="Follow-up">Follow-up</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Response</label>
            <Textarea
              value={editedQuestion.response}
              onChange={(e) => setEditedQuestion({ ...editedQuestion, response: e.target.value })}
              placeholder="Enter response (optional)..."
              rows={2}
            />
          </div>
        </div>
      </div>
    );
  }

  // Default display mode with quick edit
  return (
    <div className="border rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1 cursor-pointer" onClick={() => setIsEditMode(true)}>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-blue-600">{question.question_number}</span>
            <Badge className={getStatusColor(question.status)}>
              {question.status}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-sm">
              <span className="font-medium text-gray-600">Q:</span> {question.question_text || "(Click to edit)"}
            </p>
            {question.response && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">A:</span> {question.response}
              </p>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(question.id);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}
